import { EmbedWrapper } from '@embeds/components/Layouts/EmbedWrapper';
import { Protected } from '@embeds/components/Protected';
import {
  type ActiveOfferSetRes,
  type SiteResponse,
  v1SitesDetail,
  v1SitesOfferSetDetail,
} from '@on3/api/src';
import { ModalProvider } from '@on3/ui-lib/components/Modal/Provider';
import { SubscriptionProvider } from '@on3/ui-lib/components/Subscription/Provider';
import { Recurly } from '@on3/ui-lib/components/Subscription/Recurly';
import { type ISiteContext } from '@on3/ui-lib/contexts/SiteProvider';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import { AuthProvider, proxyApi, SiteProvider } from '@on3/ui-lib/index';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { type GetServerSideProps } from 'next';
import { useEffect, useState } from 'react';

interface ProtectedPageProps {
  site: SiteResponse | null;
  offerSet: ActiveOfferSetRes | null;
}

const ProtectedPage = ({ site, offerSet }: ProtectedPageProps) => {
  const [cleanCtx, setCleanCtx] = useState(false);
  const siteContext = { currentSite: site } as ISiteContext;

  useEffect(() => {
    if (!cleanCtx) {
      // Remove any previous tokens stored
      const COOKIES_TO_CLEAR = ['token', 'refreshToken'];

      COOKIES_TO_CLEAR.forEach((cookieName) => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });

      setCleanCtx(true);
    }
  }, [cleanCtx]);

  if (!cleanCtx) return null;

  return (
    <Recurly>
      <SiteProvider {...siteContext}>
        <EmbedWrapper mode="transparent" site={site}>
          <AuthProvider>
            <SubscriptionProvider site={site} subscriptionData={offerSet}>
              <ModalProvider>
                <Protected />
              </ModalProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </EmbedWrapper>
      </SiteProvider>
    </Recurly>
  );
};

export const getServerSideProps: GetServerSideProps<
  ProtectedPageProps
> = async (ctx) => {
  try {
    const { siteKey } = ctx.query as {
      siteKey?: string;
    };

    const [_site, _offerSet] = await Promise.allSettled([
      v1SitesDetail(proxyApi, { key: Number(siteKey || 0) }),
      v1SitesOfferSetDetail(proxyApi, { siteKey: Number(siteKey || 0) }),
    ]);

    return {
      props: {
        site: getFulfilledValue(_site),
        offerSet: getFulfilledValue(_offerSet),
      },
    };
  } catch (error) {
    return { props: { site: null, offerSet: null } };
  }
};

export default withAuthentication(ProtectedPage);
